import { useEffect, useState } from "react";
import api, { fmtMXN, fmtMXNFull, fmtDate } from "@/lib/api";
import { CalendarClock, AlertTriangle, Clock, CalendarDays, ArrowRight, Repeat, ChevronDown, ChevronUp } from "lucide-react";
import { Link } from "react-router-dom";

const BUCKET_META = {
  vencido: {
    label: "Vencidos",
    sub: "Pago atrasado",
    color: "#dc2626",
    bg: "rgba(220,38,38,0.12)",
    border: "rgba(220,38,38,0.4)",
    Icon: AlertTriangle,
  },
  esta_semana: {
    label: "Esta semana",
    sub: "Próximos 7 días",
    color: "#f59e0b",
    bg: "rgba(245,158,11,0.10)",
    border: "rgba(245,158,11,0.4)",
    Icon: Clock,
  },
  este_mes: {
    label: "Este mes",
    sub: "Resto del mes en curso",
    color: "#22d3ee",
    bg: "rgba(34,211,238,0.08)",
    border: "rgba(34,211,238,0.4)",
    Icon: CalendarDays,
  },
  proximos_30: {
    label: "Próximos 30 días",
    sub: "Más allá del mes actual",
    color: "#a78bfa",
    bg: "rgba(167,139,250,0.10)",
    border: "rgba(167,139,250,0.4)",
    Icon: CalendarClock,
  },
};
const BUCKET_ORDER = ["vencido", "esta_semana", "este_mes", "proximos_30"];

export default function UpcomingPayments() {
  const [data, setData] = useState(null);
  const [openBucket, setOpenBucket] = useState("vencido");

  useEffect(() => {
    let active = true;
    api.get("/dashboard/upcoming-payments")
      .then((res) => { if (active) setData(res.data); })
      .catch(() => { if (active) setData({ buckets: {}, totals: {}, total_outstanding: 0, counts: {} }); });
    return () => { active = false; };
  }, []);

  if (!data) {
    return (
      <div className="panel p-5" data-testid="upcoming-payments-loading">
        <div className="label-mono mb-3" style={{ color: "var(--cyan)" }}>// PAGOS PRÓXIMOS</div>
        <div className="text-xs text-slate-500">Cargando…</div>
      </div>
    );
  }

  const totalOutstanding = data.total_outstanding || 0;

  return (
    <div className="panel p-5" data-testid="upcoming-payments">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="label-mono mb-1" style={{ color: "var(--cyan)" }}>// PAGOS PRÓXIMOS</div>
          <div className="text-base sm:text-lg font-display font-bold text-slate-100">Compromisos por pagar</div>
        </div>
        <div className="text-right">
          <div className="label-mono text-[10px] text-slate-400">Total comprometido</div>
          <div className="font-mono font-bold text-lg sm:text-xl tabular-nums" style={{ color: totalOutstanding > 0 ? "#f59e0b" : "#16a34a" }} data-testid="upcoming-total">
            {fmtMXNFull(totalOutstanding)}
          </div>
        </div>
      </div>

      {/* Bucket summary chips */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
        {BUCKET_ORDER.map((key) => {
          const meta = BUCKET_META[key];
          const count = data.counts?.[key] ?? 0;
          const total = data.totals?.[key] ?? 0;
          const active = openBucket === key;
          return (
            <button
              key={key}
              data-testid={`upcoming-bucket-${key}`}
              onClick={() => setOpenBucket(active ? null : key)}
              className="text-left p-3 rounded-sm transition-all"
              style={{
                background: active ? meta.bg : "rgba(255,255,255,0.02)",
                border: `1px solid ${active ? meta.border : "rgba(255,255,255,0.06)"}`,
                boxShadow: active ? `inset 0 0 18px ${meta.bg}` : "none",
              }}
            >
              <div className="flex items-center gap-2 mb-1.5">
                <meta.Icon className="w-4 h-4" style={{ color: meta.color }} />
                <span className="label-mono text-[10px]" style={{ color: meta.color }}>{meta.label}</span>
              </div>
              <div className="font-mono font-bold tabular-nums text-base text-slate-100">{fmtMXN(total)}</div>
              <div className="text-[10px] text-slate-500 mt-0.5 flex items-center justify-between">
                <span>{count} {count === 1 ? "pago" : "pagos"}</span>
                {active ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </div>
            </button>
          );
        })}
      </div>

      {/* Detail list of the open bucket */}
      {openBucket && BUCKET_META[openBucket] && (
        <BucketDetail
          bucket={openBucket}
          items={data.buckets?.[openBucket] || []}
        />
      )}

      {totalOutstanding === 0 && (
        <div className="text-xs text-slate-500 text-center py-6">
          ✓ No hay compromisos de pago pendientes. ¡Todo al día!
        </div>
      )}
    </div>
  );
}

function BucketDetail({ bucket, items }) {
  const meta = BUCKET_META[bucket];
  if (!items.length) {
    return (
      <div className="text-xs text-slate-500 italic px-3 py-4 text-center" style={{ background: "rgba(255,255,255,0.02)" }}>
        Sin pagos en este bloque.
      </div>
    );
  }
  return (
    <div data-testid={`upcoming-detail-${bucket}`}>
      <div className="space-y-1.5">
        {items.slice(0, 8).map((it) => (
          <PaymentRow key={`${it.source}-${it.id}`} item={it} color={meta.color} />
        ))}
        {items.length > 8 && (
          <div className="text-[11px] text-slate-500 italic text-center pt-2">
            …y {items.length - 8} más. Ve a Gastos para administrarlos.
          </div>
        )}
      </div>
      <div className="mt-3 flex items-center justify-end">
        <Link to="/expenses" className="text-xs uppercase tracking-widest font-semibold flex items-center gap-1 hover:underline" style={{ color: meta.color }}>
          Ir a Gastos <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
    </div>
  );
}

function PaymentRow({ item, color }) {
  const sourceLabel = item.source === "admin" ? "ADMIN" : "OBRA";
  const sourceColor = item.source === "admin" ? "var(--magenta)" : "var(--cyan)";
  return (
    <div
      className="px-3 py-2 rounded-sm flex items-center justify-between gap-2 text-xs"
      style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.04)" }}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
          <span
            className="label-mono text-[9px] px-1.5 py-0.5 rounded-sm"
            style={{ color: sourceColor, border: `1px solid ${sourceColor}`, background: "rgba(0,0,0,0.25)" }}
          >
            {sourceLabel}
          </span>
          {item.is_recurring && (
            <span className="label-mono text-[9px] flex items-center gap-0.5" style={{ color: "#a78bfa" }} title="Gasto recurrente mensual">
              <Repeat className="w-2.5 h-2.5" /> REC
            </span>
          )}
          <span className="label-mono text-[9px]" style={{ color: item.status === "aprobado" ? "var(--green)" : "#d97706" }}>
            {item.status === "aprobado" ? "✓ Aprobado" : "⏳ Pendiente"}
          </span>
        </div>
        <div className="font-semibold text-slate-100 truncate">{item.description}</div>
        <div className="text-[10px] text-slate-500 truncate">
          {item.vendor || "—"}
          {item.project_label && <span> · {item.project_label}</span>}
        </div>
      </div>
      <div className="text-right flex-shrink-0">
        <div className="font-mono font-bold tabular-nums text-slate-100">{fmtMXN(item.amount)}</div>
        <div className="label-mono text-[9px]" style={{ color }}>{fmtDate(item.due_date)}</div>
      </div>
    </div>
  );
}
